import { DialogDescription } from '@radix-ui/react-dialog';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useGetInsightsExport } from '../../api/insights';
import { useAuthentication } from '../../auth';
import { ClinicSelectInput } from '../../components/app/ClinicSelectInput';
import { Button } from '../../components/Button';
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/Dialog';
import { dateRanges } from '../../components/Input';
import { DateRangePickerInput } from '../../components/Input/DateRangePickerInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/Input/Select';
import { InputLabel } from '../../components/Input/TextInput';
import { useDateRange } from '../../hooks/useDateRange';
import { useToaster } from '../../hooks/useToaster';
import { getTenantIds } from '../../stores/slices/preferences';

interface ExportPatientDataModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ExportPatientDataModal = ({ open, onOpenChange }: ExportPatientDataModalProps) => {
  const toaster = useToaster();
  const [exportInsights, { isLoading }] = useGetInsightsExport();
  const currentUser = useAuthentication();
  const selectedTenantIds = useSelector(getTenantIds());
  const [tenantIds, setTenantIds] = useState<string[] | undefined>(selectedTenantIds);
  const [dateRange, setDateRange] = useState<[Date, Date] | null>(dateRanges.all.value);
  const { dateFrom, dateTo } = useDateRange(dateRange);
  const [format, setFormat] = useState('xlsx');

  if (!open && tenantIds !== selectedTenantIds) {
    setTenantIds(selectedTenantIds);
  }

  const onDownload = useCallback(async () => {
    try {
      await exportInsights({
        tenantIds: tenantIds?.length ? tenantIds : undefined,
        dateFrom,
        dateTo,
        format
      }).unwrap();
      onOpenChange(false);
      toaster.success('Your data export is ready and will download shortly.');
    } catch {
      toaster.error('Failed to export the data. Please try again.');
    }
  }, [tenantIds, dateFrom, dateTo, format, exportInsights, onOpenChange, toaster]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]" data-testid="export-data-modal">
        <DialogHeader>
          <DialogTitle>Export Patient Data</DialogTitle>
          <DialogDescription>Please confirm the patient data to export.</DialogDescription>
        </DialogHeader>

        {currentUser.subtenants && currentUser.subtenants.length > 1 && (
          <div>
            <InputLabel>Clinics*</InputLabel>
            <ClinicSelectInput
              defaultValue={tenantIds}
              onChange={setTenantIds}
              placeholder="All clinics"
              className="w-full md:w-[380px]"
            />
          </div>
        )}

        <div>
          <InputLabel>Date range*</InputLabel>

          <div className="md:max-w-[380px]">
            <DateRangePickerInput
              value={dateRange}
              onChange={setDateRange}
              testId="date-range-input"
              className="w-full"
            />
          </div>
        </div>

        <div>
          <InputLabel>File format*</InputLabel>

          <div>
            <Select value={format} onValueChange={(value) => setFormat(value)}>
              <SelectTrigger className="h-[42px] w-full md:w-[380px]" autoFocus>
                <SelectValue placeholder="Select a file type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="csv">CSV</SelectItem>
                <SelectItem value="xlsx">XLSX</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="accent"
            onClick={onDownload}
            isLoading={isLoading}
            disabled={isLoading}
            data-testid="export-data-download-button"
          >
            Download
          </Button>
          <DialogClose asChild>
            <Button variant="transparent">Cancel</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
